import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PackageCheck } from "lucide-react";

// Compact "Returns this week" manager widget — received / approved / quarantined / awaiting, with a
// 7-day received sparkline. Company-scoped (active company timezone). Only for returns-inspect users.
export default function ReturnsWeekWidget() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [a, setA] = useState(null);
  const canInspect = user?.is_super || (user?.permissions || []).includes("mobile.returns.inspect");

  useEffect(() => {
    if (!canInspect) return;
    let on = true;
    api.get("/returns/analytics").then((r) => on && setA(r.data)).catch(() => {});
    return () => { on = false; };
  }, [canInspect]);

  if (!canInspect || !a) return null;
  const max = Math.max(1, ...a.daily.map((d) => d.count));

  return (
    <button onClick={() => nav("/returns")} data-testid="returns-week-widget"
      className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-indigo-300 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><PackageCheck className="w-4 h-4" /></span>
        <div className="font-semibold text-slate-800">Returns this week</div>
        <div className="ml-auto text-xs text-slate-400">tap to inspect</div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-3">
        <Stat label="Received" value={a.received_this_week} color="text-slate-900" testid="rw-received" />
        <Stat label="Approved" value={a.approved_resale} color="text-emerald-600" testid="rw-approved" />
        <Stat label="Quarantined" value={a.quarantined} color="text-red-600" testid="rw-quarantined" />
        <Stat label="Awaiting" value={a.awaiting_inspection} color="text-amber-600" testid="rw-awaiting" />
      </div>
      <div className="flex items-end gap-1 h-12" data-testid="rw-sparkline">
        {a.daily.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
            <div className="w-full rounded-t bg-indigo-400" style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count ? 4 : 1 }} />
            <span className="text-[9px] text-slate-400 mt-0.5">{d.date.slice(8)}</span>
          </div>
        ))}
      </div>
    </button>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div>
      <div className={`text-xl font-bold ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    </div>
  );
}
